import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

// ─────────────────────────────────────────────────────────────────────────────
// CopyableComponent (ui-copyable)
// ─────────────────────────────────────────────────────────────────────────────
// Reusable click-to-copy affordance for inline values (IDs, references, codes…).
//  - A discreet copy icon (in a bordered box) fades in on hover.
//  - On click it copies `value`, turns the box green, swaps the icon to a plain
//    tick and briefly shows the "Copied" tooltip, then reverts.
//  - Copy goes through the CDK Clipboard, which works in non-secure/HTTP
//    contexts too (no manual execCommand fallback needed).
//  - The tooltip uses Angular Material (a body-level overlay) so it is never
//    clipped by table/scroll `overflow` and always renders on top.
//  - Click propagation is stopped so it can live inside clickable rows.
//
// Two sizes:
//   - "small" — dense contexts such as table cells (default), tooltip above.
//   - "big"   — titles / page & panel headers, tooltip below.
//
// Usage:
//   <ui-copyable [value]="quote.external_id" label="ID ERP">
//     {{ quote.external_id || '—' }}
//   </ui-copyable>
//
//   <ui-copyable size="big" [value]="quotation?.name" label="Order ID"></ui-copyable>
// ─────────────────────────────────────────────────────────────────────────────

export type CopyableSize = 'small' | 'big';

@Component({
  selector: 'ui-copyable',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatTooltipModule, SvgIconComponent],
  templateUrl: './copyable.component.html',
  styleUrls: ['./copyable.component.scss'],
})
export class CopyableComponent implements OnDestroy {
  /** Raw value placed on the clipboard. When empty, the copy affordance is hidden. */
  @Input() value: string | number | null | undefined;

  /** Contextual label used in the tooltip / aria-label, e.g. "ID ERP" → "Copy ID ERP". */
  @Input() label = '';

  /** Visual scale: "small" for tables (default), "big" for titles / headers. */
  @Input() size: CopyableSize = 'small';

  /** How long the "Copied" confirmation stays visible, in ms. */
  @Input() confirmDuration = 1500;

  @ViewChild('tooltip') private tooltip?: MatTooltip;

  copied = false;

  private resetTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private clipboard: Clipboard,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  // ── Derived state ──────────────────────────────────────────────────────────
  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && `${this.value}`.trim().length > 0;
  }

  /** Icon scale mapped from the component size ("small" → 12px, "big" → 16px). */
  get iconSize(): 'small' | 'medium' {
    return this.size === 'big' ? 'medium' : 'small';
  }

  /** Tooltip / aria-label text, reflecting the current copied state and label. */
  get tooltipText(): string {
    const base = this.translate.instant(this.copied ? 'common.copied' : 'common.copy');
    return this.label ? `${base} ${this.label}` : base;
  }

  /** Small sits in dense rows (tooltip above); big is a header (tooltip below). */
  get tooltipPosition(): 'above' | 'below' {
    return this.size === 'big' ? 'below' : 'above';
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  copy(event: MouseEvent): void {
    // Live inside clickable table rows without triggering row navigation.
    event.stopPropagation();
    if (!this.hasValue) return;

    if (!this.clipboard.copy(`${this.value}`)) return;

    this.copied = true;
    this.cdr.markForCheck();

    // Surface the "Copied" confirmation on top of everything, even mid-hover.
    if (this.tooltip) {
      this.tooltip.message = this.tooltipText;
      this.tooltip.show();
    }

    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.copied = false;
      this.cdr.markForCheck();
    }, this.confirmDuration);
  }

  ngOnDestroy(): void {
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }
}
